import { format, setCursor, event } from './utils';
import assign from './assign';
import defaults from './options';

function addThousandSeparator(integer, separator) {
  return integer.replace(/(\d)(?=(?:\d{3})+\b)/gm, `$1${separator}`);
}

function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function countOccurrences(str, sub) {
  const re = new RegExp(escapeRegExp(sub), 'g');
  const matches = str.match(re);
  return matches ? matches.length : 0;
}

export default function (el, binding) {
  if (!binding.value) return;
  const opt = assign(defaults, binding.value);

  if (el.tagName.toUpperCase() !== 'INPUT') {
    const els = el.getElementsByTagName('input');
    if (els.length !== 1) return;

    // eslint-disable-next-line prefer-destructuring, no-param-reassign
    el = els[0];
  }

  el.addEventListener('focus', () => {
    if (!el._justFocused) {
      el.select();
      el._justFocused = true;
    }
  });

  el.addEventListener('mousedown', () => {
    if (el._justFocused) {
      el._justFocused = false;
    }
  });

  el.addEventListener('blur', () => {
    el._justFocused = false;
  });

  let skipFormat = false;
  let fullReset = false;

  el.onkeydown = function (e) {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End',
      'Enter', 'Escape', ',', '.', '-',
    ];
    const isDigit = /\d/.test(e.key);
    const isAllowedKey = allowedKeys.includes(e.key);
    const isModifierKey = e.ctrlKey || e.metaKey;
    const isClipboardOperation = isModifierKey && (
      e.key === 'v' || e.key === 'c' || e.key === 'x' || e.key === 'a'
    );

    if (e.key === '-') {
      const isAtStart = el.selectionStart === opt.prefix.length;
      const isAllSelected = el.selectionStart === 0 && el.selectionEnd === el.value.length;
      const hasMinusAlready = el.value.charAt(opt.prefix.length) === '-';

      if (hasMinusAlready) {
        e.preventDefault();

        if (isAllSelected) {
          const decimalIndex = el.value.indexOf(opt.decimal);
          let wholePart = '0';
          let decimalPart = '0'.repeat(opt.precision);

          if (decimalIndex !== -1) {
            const formattedInteger = el.value.slice(opt.prefix.length, decimalIndex);
            const strippedInteger = formattedInteger.replace(/-/g, '');
            wholePart = strippedInteger || '0';

            decimalPart = el.value.slice(decimalIndex + 1, el.value.length - (opt.suffix ? opt.suffix.length : 0));
            decimalPart = decimalPart.padEnd(opt.precision, '0').slice(0, opt.precision);
          }

          const newValue = `${opt.prefix}-${wholePart}${opt.decimal}${decimalPart}${opt.suffix || ''}`;
          el.value = newValue;
          setCursor(el, opt.prefix.length + 1);

          el.dispatchEvent(event('change'));
        }
        return;
      }

      if (!isAtStart && !isAllSelected) {
        e.preventDefault();
        return;
      }

      if (isAtStart) {
        e.preventDefault();
        skipFormat = true;

        const decimalIndex = el.value.indexOf(opt.decimal);
        const integerPart = el.value.slice(opt.prefix.length, decimalIndex);
        let currentDecimal = '0'.repeat(opt.precision);

        if (decimalIndex !== -1) {
          currentDecimal = el.value.slice(decimalIndex + 1, el.value.length - (opt.suffix ? opt.suffix.length : 0));
          currentDecimal = currentDecimal.padEnd(opt.precision, '0').slice(0, opt.precision);
        }

        const newValue = `${opt.prefix}-${integerPart}${opt.decimal}${currentDecimal}${opt.suffix || ''}`;
        el.value = newValue;
        setCursor(el, opt.prefix.length + 1);

        el.dispatchEvent(event('change'));
        return;
      }
    }

    if (isModifierKey && e.key === 'v') {
      if (!(el.selectionStart === 0 && el.selectionEnd === el.value.length)) {
        el.select();
      }
    }

    if (!isDigit && !isAllowedKey && !isClipboardOperation) {
      e.preventDefault();
      return;
    }

    let caretPos = el.selectionStart;
    let { value } = el;
    let decimalIndex = value.indexOf(opt.decimal);

    if (/\d/.test(e.key)) {
      const hasMinusPrefix = value.charAt(opt.prefix.length) === '-';

      if (el.selectionStart === 0 && el.selectionEnd === value.length) {
        e.preventDefault();
        const initialValue = `${opt.prefix}0${opt.decimal}${'0'.repeat(opt.precision)}${opt.suffix || ''}`;
        el.value = initialValue;
        setCursor(el, opt.prefix.length);
        fullReset = true;
      }
      if (fullReset) {
        caretPos = el.selectionStart;
        value = el.value;
        decimalIndex = value.indexOf(opt.decimal);
        fullReset = false;
      }

      if (hasMinusPrefix && caretPos === opt.prefix.length + 1) {
        e.preventDefault();
        skipFormat = true;

        const formattedInteger = value.slice(opt.prefix.length + 1, decimalIndex);
        let currentDecimal = value.slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));
        currentDecimal = currentDecimal.padEnd(opt.precision, '0').slice(0, opt.precision);

        // Kontrola, zda je celočíselná část nula - pokud ano, nahradíme ji, jinak přidáme před
        const isZero = formattedInteger === '0' || formattedInteger === '';

        const newValue = isZero
          ? `${opt.prefix}-${e.key}${opt.decimal}${currentDecimal}${opt.suffix || ''}`
          : `${opt.prefix}-${e.key}${formattedInteger}${opt.decimal}${currentDecimal}${opt.suffix || ''}`;

        el.value = newValue;
        setCursor(el, opt.prefix.length + 2);

        el.dispatchEvent(event('change'));
        return;
      }
    }

    if (e.key === 'Backspace' && caretPos > opt.prefix.length && caretPos <= decimalIndex) {
      e.preventDefault();
      skipFormat = true;

      const formattedInteger = value.slice(opt.prefix.length, decimalIndex);
      let rawWhole = formattedInteger.replace(new RegExp(escapeRegExp(opt.thousands), 'g'), '');
      const caretInInteger = caretPos - opt.prefix.length;
      const sepCount = countOccurrences(formattedInteger.slice(0, caretInInteger), opt.thousands);
      const rawIndex = caretInInteger - sepCount;

      if (rawIndex > 0) {
        const deletionIndex = rawIndex - 1;
        rawWhole = rawWhole.slice(0, deletionIndex) + rawWhole.slice(deletionIndex + 1);

        const formattedWhole = addThousandSeparator(rawWhole || '0', opt.thousands);
        let currentDecimal = value.slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));
        currentDecimal = currentDecimal.padEnd(opt.precision, '0').slice(0, opt.precision);

        const newValue = opt.prefix + formattedWhole + opt.decimal + currentDecimal + (opt.suffix || '');
        el.value = newValue;

        const rawSubstr = rawWhole.slice(0, deletionIndex);
        const formattedCaretWhole = addThousandSeparator(rawSubstr, opt.thousands);
        const newCaretPos = opt.prefix.length + formattedCaretWhole.length;
        setCursor(el, newCaretPos);

        el.dispatchEvent(event('change'));
        return;
      }
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && decimalIndex !== -1 && caretPos > decimalIndex) {
      e.preventDefault();
      skipFormat = true;

      const currentDecimal = value
        .slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));
      const relIndex = caretPos - (decimalIndex + 1);
      let deletionIndex;
      let newCaret;
      const suffixPart = (opt.suffix && value.endsWith(opt.suffix)) ? opt.suffix : '';

      if (e.key === 'Delete') {
        if (relIndex === 0) {
          deletionIndex = 0;
          newCaret = caretPos;
        } else if (relIndex >= currentDecimal.length) {
          return;
        } else {
          deletionIndex = relIndex;
          newCaret = caretPos;
        }
      } else if (e.key === 'Backspace') {
        if (relIndex === 0) {
          const formattedInteger = value.slice(opt.prefix.length, decimalIndex);
          let rawWhole = formattedInteger.replace(new RegExp(escapeRegExp(opt.thousands), 'g'), '');

          if (rawWhole.length > 1) {
            rawWhole = rawWhole.slice(0, -1);
          } else {
            rawWhole = '0';
          }

          const formattedWhole = addThousandSeparator(rawWhole, opt.thousands);
          const newValue = opt.prefix + formattedWhole + opt.decimal + currentDecimal + suffixPart;
          el.value = newValue;

          const newCaretPos = opt.prefix.length + formattedWhole.length;
          setCursor(el, newCaretPos);

          el.dispatchEvent(event('change'));
          return;
        }
        deletionIndex = relIndex - 1;
        newCaret = caretPos - 1;
      }

      let newDecimal = currentDecimal;
      if (deletionIndex >= 0 && deletionIndex < currentDecimal.length) {
        newDecimal = currentDecimal.substring(0, deletionIndex)
                     + currentDecimal.substring(deletionIndex + 1);
      }

      if (newDecimal.length < opt.precision) {
        newDecimal = newDecimal.padEnd(opt.precision, '0');
      }

      const integerPart = value.slice(0, decimalIndex);
      const newValue = integerPart + opt.decimal + newDecimal + suffixPart;
      el.value = newValue;
      setCursor(el, newCaret);

      el.dispatchEvent(event('change'));
      return;
    }

    if (/\d/.test(e.key)) {
      const hasMinusPrefix = value.charAt(opt.prefix.length) === '-';

      if (el.selectionStart === 0 && el.selectionEnd === value.length) {
        e.preventDefault();
        const initialValue = `${opt.prefix}0${opt.decimal}${'0'.repeat(opt.precision)}${opt.suffix || ''}`;
        el.value = initialValue;
        setCursor(el, opt.prefix.length);
        fullReset = true;
      }
      if (fullReset) {
        caretPos = el.selectionStart;
        value = el.value;
        decimalIndex = value.indexOf(opt.decimal);
        fullReset = false;
      }

      if (hasMinusPrefix && caretPos === opt.prefix.length + 1) {
        e.preventDefault();
        skipFormat = true;

        const formattedInteger = value.slice(opt.prefix.length + 1, decimalIndex);
        let currentDecimal = value.slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));
        currentDecimal = currentDecimal.padEnd(opt.precision, '0').slice(0, opt.precision);

        // Kontrola, zda je celočíselná část nula - pokud ano, nahradíme ji, jinak přidáme před
        const isZero = formattedInteger === '0' || formattedInteger === '';

        const newValue = isZero
          ? `${opt.prefix}-${e.key}${opt.decimal}${currentDecimal}${opt.suffix || ''}`
          : `${opt.prefix}-${e.key}${formattedInteger}${opt.decimal}${currentDecimal}${opt.suffix || ''}`;

        el.value = newValue;
        setCursor(el, opt.prefix.length + 2);

        el.dispatchEvent(event('change'));
        return;
      }

      if (caretPos <= decimalIndex) {
        e.preventDefault();
        skipFormat = true;

        const formattedInteger = value.slice(opt.prefix.length, decimalIndex);
        let rawWhole = formattedInteger.replace(new RegExp(escapeRegExp(opt.thousands), 'g'), '');
        const caretInInteger = caretPos - opt.prefix.length;
        const sepCount = countOccurrences(formattedInteger.slice(0, caretInInteger), opt.thousands);
        let rawIndex = caretInInteger - sepCount;

        if (rawWhole === '0') {
          rawWhole = '';
          rawIndex = 0;
        }

        rawWhole = rawWhole.slice(0, rawIndex) + e.key + rawWhole.slice(rawIndex);
        const formattedWhole = addThousandSeparator(rawWhole || '0', opt.thousands);

        let currentDecimal = value.slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));
        currentDecimal = currentDecimal.padEnd(opt.precision, '0').slice(0, opt.precision);

        const newValue = opt.prefix + formattedWhole + opt.decimal + currentDecimal + (opt.suffix || '');
        el.value = newValue;

        const rawSubstr = rawWhole.slice(0, rawIndex + 1);
        const formattedCaretWhole = addThousandSeparator(rawSubstr, opt.thousands);
        let newCaretPos = opt.prefix.length + formattedCaretWhole.length;
        newCaretPos = Math.min(newCaretPos, newValue.indexOf(opt.decimal));
        setCursor(el, newCaretPos);

        el.dispatchEvent(event('change'));
      } else {
        const relIndex = caretPos - decimalIndex - 1;
        e.preventDefault();
        skipFormat = true;

        const currentDecimal = value
          .slice(decimalIndex + 1, value.length - (opt.suffix ? opt.suffix.length : 0));

        const newDecimalArr = currentDecimal.split('');
        while (relIndex >= newDecimalArr.length) {
          newDecimalArr.push('0');
        }

        newDecimalArr[relIndex] = e.key;
        const newDecimal = newDecimalArr.join('');

        const integerPart = value.slice(0, decimalIndex);
        const suffixPart = (opt.suffix && value.endsWith(opt.suffix)) ? opt.suffix : '';
        const newValue = integerPart + opt.decimal + newDecimal + suffixPart;
        el.value = newValue;
        setCursor(el, decimalIndex + 1 + relIndex + 1);

        el.dispatchEvent(event('change'));
      }
    }
  };

  el.oninput = function () {
    if (skipFormat) {
      skipFormat = false;
      return;
    }
    const cursorPos = el.selectionStart;
    let rawValue = el.value;

    if (rawValue.startsWith(opt.prefix)) {
      rawValue = rawValue.slice(opt.prefix.length);
    }
    if (opt.suffix && rawValue.endsWith(opt.suffix)) {
      rawValue = rawValue.slice(0, -opt.suffix.length);
    }

    const parts = rawValue.split(opt.decimal);
    const hasCustomDecimal = parts.length > 1 && parts[1].length > opt.precision;

    const formatted = format(rawValue, {
      ...opt,
      ...(hasCustomDecimal ? { precision: parts[1].length } : {}),
    });
    el.value = formatted;

    setCursor(el, cursorPos);
    el.dispatchEvent(event('change'));
  };

  el.oninput();
  el.dispatchEvent(event('input'));
}
